// Dynamic AQI data from API

let cachePromise = null;
let cacheTime = 0;
const CACHE_TTL = 30000; // 30 seconds

export const fetchMapData = async (forceRefresh = false, pageNumber = null, pageSize = null) => {
    const isPaginated = pageNumber !== null || pageSize !== null;
    const now = Date.now();
    if (!isPaginated && cachePromise && !forceRefresh && (now - cacheTime < CACHE_TTL)) {
        return cachePromise;
    }

    const fetchFn = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            let url = `${process.env.REACT_APP_API_BASE_URL}/pole-latest-pm10-map/`;
            const params = new URLSearchParams();
            if (pageNumber) params.append('page_number', pageNumber);
            if (pageSize) params.append('page_size', pageSize);
            else if (!pageNumber) {
                params.append('page_size', '1000');
            }
            const queryString = params.toString();
            if (queryString) {
                url += `?${queryString}`;
            }

            const response = await fetch(url, {
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json'
                }
            });
            const json = await response.json();

            // Fetch poles list data for site/location/status hydration from verified all-pole-airpulse endpoint
            let polesList = [];
            try {
                const poleListJson = await fetchPolesLiveStatus();
                if ((poleListJson.status || poleListJson.success) && Array.isArray(poleListJson.data)) {
                    polesList = poleListJson.data;
                }
            } catch (err) {
                console.error('Error fetching pole list data for map hydration:', err);
            }

            const poleLookup = new Map();
            polesList.forEach(p => {
                const key = p.pole_id || p.poleNumber || p.id;
                if (key) {
                    poleLookup.set(String(key).trim().toUpperCase(), p);
                }
            });

            if ((json.success || json.status) && json.data) {
                if (json.data.length > 0) {
                    console.log('Sample item from pole-latest-pm10-map:', json.data[0]);
                }
                const mappedData = json.data.map((item, index) => {
                    const lookupKey = item.pole_id ? String(item.pole_id).trim().toUpperCase() : '';
                    const matchedPole = poleLookup.get(lookupKey);

                    const dominentpol = "PM10";
                    const pm10Val = item.pm10;

                    // Determine status: if pm10 is null, mark offline. Otherwise use matched status or default 'online'
                    const rawStatus = item.status_text || matchedPole?.status_text || matchedPole?.poleStatus || matchedPole?.status || '';
                    const lowerRawStatus = rawStatus.toLowerCase();
                    let poleStatus = 'online';
                    let statusText = 'Online';

                    if (lowerRawStatus.includes('awaiting')) {
                        poleStatus = 'Awaiting Connection';
                        statusText = 'Awaiting Connection';
                    } else if (lowerRawStatus.includes('offline') || pm10Val === null) {
                        poleStatus = 'offline';
                        statusText = 'Offline';
                    } else {
                        poleStatus = 'online';
                        statusText = rawStatus || 'Online';
                    }

                    const rawLastTelemetry = item.DateStamp || item.date_stamp || item.last_telemetry || item.lastTelemetry || item.last_telemetry_received || item.received_datetime || item.received_utc || item.last_updated || item.lastUpdated || item.timestamp || item.Date || item.date || matchedPole?.DateStamp || matchedPole?.date_stamp || matchedPole?.last_telemetry || matchedPole?.lastTelemetry || matchedPole?.last_telemetry_received || matchedPole?.received_datetime || matchedPole?.last_updated || matchedPole?.timestamp || null;
                    const rawTelemetryWarnings = item.telemetry_warnings || item.telemetryWarnings || item.warnings || matchedPole?.telemetry_warnings || matchedPole?.telemetryWarnings || matchedPole?.warnings || 0;

                    return {
                        ...item,
                        uid: item.pole_id || `station_${index}`,
                        poleNumber: item.pole_id,
                        name: item.pole_id,
                        poleStatusId: poleStatus === 'offline' ? 1 : (poleStatus === 'Awaiting Connection' ? 2 : 3),
                        poleStatus,
                        status_text: statusText,
                        bucketStartUtc: rawLastTelemetry,
                        lastTelemetry: rawLastTelemetry,
                        last_telemetry: rawLastTelemetry,
                        telemetryWarnings: rawTelemetryWarnings,
                        telemetry_warnings: rawTelemetryWarnings,
                        dailyVolumeCubicFeet: matchedPole?.total_air_cleaned_cuft_last_24h ?? matchedPole?.dailyVolumeCubicFeet ?? matchedPole?.reduction ?? 0,
                        siteName: matchedPole?.site || matchedPole?.siteName || matchedPole?.zone || '',
                        locationName: matchedPole?.location || matchedPole?.locationName || matchedPole?.ward || '',
                        lat: item.latitude,
                        lon: item.longitude,
                        time: rawLastTelemetry,
                        dominentpol,
                        temperature_c: matchedPole?.temperature_c,
                        humidity_percent: matchedPole?.humidity_percent,
                        aqiBefore: {
                            'PM10': matchedPole?.pm10 ?? 0,
                            'PM2.5': matchedPole?.pm25 ?? 0,
                            'AQI': matchedPole?.aqi ?? 0
                        },
                        aqiAfter: {
                            'PM10': pm10Val ?? 0,
                            'PM2.5': matchedPole?.pm25 ?? 0,
                            'AQI': pm10Val ?? 0
                        },
                        aqi: pm10Val ?? 0
                    };
                });

                if (isPaginated) {
                    return {
                        data: mappedData,
                        count: json.count ?? mappedData.length
                    };
                }
                return mappedData;
            }
            return isPaginated ? { data: [], count: 0 } : [];
        } catch (error) {
            console.error('Error fetching map data:', error);
            if (!isPaginated) cachePromise = null;
            return isPaginated ? { data: [], count: 0 } : [];
        }
    };

    if (isPaginated) {
        return fetchFn();
    }

    cachePromise = fetchFn();
    cacheTime = now;
    return cachePromise;
};

let metadataPromise = null;
let metadataCacheTime = 0;
const METADATA_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

export const fetchMetadata = async (forceRefresh = false) => {
    const now = Date.now();

    // 1. Check in-memory promise/cache
    if (metadataPromise && !forceRefresh && (now - metadataCacheTime < METADATA_CACHE_TTL)) {
        return metadataPromise;
    }

    // 2. Check localStorage cache
    try {
        const cachedCountries = localStorage.getItem('cached_countries');
        const cachedSites = localStorage.getItem('cached_sites');
        const cachedStates = localStorage.getItem('cached_states');
        const cachedLocations = localStorage.getItem('cached_locations');
        const cacheTime = localStorage.getItem('metadata_cache_time');

        const isCacheValid = cacheTime && (now - parseInt(cacheTime) < METADATA_CACHE_TTL);

        if (!forceRefresh && isCacheValid && cachedCountries && cachedSites && cachedStates && cachedLocations) {
            const data = {
                countries: JSON.parse(cachedCountries),
                sites: JSON.parse(cachedSites),
                states: JSON.parse(cachedStates),
                locations: JSON.parse(cachedLocations)
            };
            metadataPromise = Promise.resolve(data);
            metadataCacheTime = parseInt(cacheTime);
            return data;
        }
    } catch (e) {
        console.warn('Failed to read metadata from localStorage:', e);
    }

    // 3. Fetch from API in parallel
    metadataPromise = (async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const headers = { 'Authorization': token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' };

            const [countriesRes, sitesRes, locationsRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_API_BASE_URL}/countries`, { headers }).then(r => r.ok ? r.json() : { data: [] }).catch(() => ({ data: [] })),
                fetch(`${process.env.REACT_APP_API_BASE_URL}/sites/`, { headers }).then(r => r.ok ? r.json() : { data: [] }).catch(() => ({ data: [] })),
                fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/`, { headers }).then(r => r.ok ? r.json() : { data: [] }).catch(() => ({ data: [] }))
            ]);

            const countries = countriesRes.status && Array.isArray(countriesRes.data) ? countriesRes.data : [];
            const sites = sitesRes.status && Array.isArray(sitesRes.data) ? sitesRes.data : [];
            const locations = locationsRes.status && Array.isArray(locationsRes.data) ? locationsRes.data : [];

            let states = [];
            if (countries.length > 0) {
                const statePromises = countries.map(async (c) => {
                    try {
                        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/states?country_id=${c.country_id}`, { headers });
                        if (res.ok) {
                            const stData = await res.json();
                            if (stData.status && Array.isArray(stData.data)) {
                                return stData.data;
                            }
                        }
                    } catch (err) {
                        console.error(`Failed to fetch states for country ${c.country_id}:`, err);
                    }
                    return [];
                });
                const resolvedStates = await Promise.all(statePromises);
                states = resolvedStates.flat();
            }

            const data = { countries, sites, states, locations };

            try {
                localStorage.setItem('cached_countries', JSON.stringify(countries));
                localStorage.setItem('cached_sites', JSON.stringify(sites));
                localStorage.setItem('cached_states', JSON.stringify(states));
                localStorage.setItem('cached_locations', JSON.stringify(locations));
                localStorage.setItem('metadata_cache_time', Date.now().toString());
            } catch (e) {
                console.warn('Failed to write metadata to localStorage:', e);
            }

            metadataCacheTime = Date.now();
            return data;
        } catch (error) {
            console.error('Error fetching metadata:', error);
            metadataPromise = null;
            return { countries: [], sites: [], states: [], locations: [] };
        }
    })();

    return metadataPromise;
};






//map card
export const fetchIndividualPoleData = async (poleId) => {
    try {
        const token = localStorage.getItem('accessToken');
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${baseUrl}/polewise-current-air-quality/?pole_id=${poleId}&_t=${Date.now()}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json'
            }
        });
        const json = await response.json();
        console.log(`fetchIndividualPoleData response for ${poleId}:`, json);

        if ((json.success || json.status) && json.data) {
            const item = json.data;
            const rawTs = item.DateStamp || item.date_stamp || item.last_telemetry || item.lastTelemetry || item.last_telemetry_received || item.received_datetime || item.received_utc || item.last_updated || item.lastUpdated || item.timestamp || item.Date || item.date || item.created_at || null;
            return {
                uid: item.pole_id,
                poleNumber: item.pole_id,
                name: item.pole_id,
                dailyVolumeCubicFeet: item.total_air_cleaned_cuft_last_24h ?? item.total_air_cleaned_so_far_today ?? item.dailyVolumeCubicFeet ?? 0,
                bucketStartUtc: rawTs,
                lastTelemetry: rawTs,
                last_telemetry: rawTs,
                aqiBefore: {
                    'PM10': item.pm10_inlet ?? 0,
                    'PM2.5': item.pm2_5_inlet ?? 0,
                    'AQI': item.pm10_inlet ?? 0
                },
                aqiAfter: {
                    'PM10': item.pm10_outlet ?? 0,
                    'PM2.5': item.pm2_5_outlet ?? 0,
                    'AQI': item.pm10_outlet ?? 0
                },
                aqi: item.pm10_outlet ?? 0
            };
        }
        return null;
    } catch (error) {
        console.error('Error fetching individual pole data:', error);
        return null;
    }
};

export const fetchPerformanceZonesData = async () => {
    try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/pole-performance/data/`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json'
            }
        });
        const json = await response.json();
        if (json.status && json.data) {
            return json.data.map(item => ({
                id: item.id || '',
                name: item.name || '',
                site_name: item.site_name || item.site || '',
                type: item.type || '',
                reduction: item.reduction || 0
            }));
        }
        return [];
    } catch (error) {
        console.error('Error fetching performance zones data:', error);
        return [];
    }
};

const formatTableTime = (rawTime) => {
    if (!rawTime) return '';
    try {
        const date = new Date(rawTime);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        const hh = String(hours).padStart(2, '0');
        return `${hh}:${minutes} ${ampm} ${day}-${month}-${year}`;
    } catch {
        return rawTime;
    }
};

export const fetchPolesData = async () => {
    try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/pole-list/data/`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json'
            }
        });
        const json = await response.json();

        if (json.status && json.data) {
            return json.data.map(item => ({
                id: item.id || '',
                zone: item.zone || '',
                ward: item.ward || '',
                aqi: item.aqi || 0,
                pm25: item.pm25 || 0,
                pm10: item.pm10 || 0,
                co2: item.co2 || 0,
                reduction: item.reduction || 0,
                status: item.status || 'Offline',
                lastUpdated: formatTableTime(item.lastUpdated)
            }));
        }
        return [];
    } catch (error) {
        console.error('Error fetching poles data:', error);
        return [];
    }
};

let liveStatusPromise = null;
let liveStatusCacheTime = 0;
const LIVE_STATUS_CACHE_TTL = 10000; // 10 seconds

export const fetchPolesLiveStatus = async (forceRefresh = false) => {
    const now = Date.now();
    if (liveStatusPromise && !forceRefresh && (now - liveStatusCacheTime < LIVE_STATUS_CACHE_TTL)) {
        return liveStatusPromise;
    }

    liveStatusPromise = (async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/all-pole-airpulse/?page_size=100000&_t=${Date.now()}`, {
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json'
                }
            });
            const json = await response.json();
            if ((json.success || json.status) && json.data) {
                return json;
            }
            return { success: false, data: [] };
        } catch (error) {
            console.error('Error fetching poles live status data:', error);
            liveStatusPromise = null;
            return { success: false, data: [] };
        }
    })();

    liveStatusCacheTime = now;
    return liveStatusPromise;
};

// Cached locations filter implementation to prevent duplicate requests on mount
let locationsFilterPromiseCache = {};
const LOCATIONS_FILTER_CACHE_TTL = 5000; // 5 seconds cache

export const fetchLocationsFilter = async (params = {}, forceRefresh = false) => {
    const queryParams = new URLSearchParams();
    if (params.country_id) queryParams.append('country_id', params.country_id);
    if (params.state_id) queryParams.append('state_id', params.state_id);
    if (params.site_id) queryParams.append('site_id', params.site_id);
    const queryString = queryParams.toString();
    const cacheKey = queryString || 'root';

    const now = Date.now();
    const cached = locationsFilterPromiseCache[cacheKey];
    if (cached && !forceRefresh && (now - cached.time < LOCATIONS_FILTER_CACHE_TTL)) {
        return cached.promise;
    }

    const promise = (async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const headers = {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json'
            };
            const url = `${process.env.REACT_APP_API_BASE_URL}/filters/locations/${queryString ? '?' + queryString : ''}`;
            const response = await fetch(url, { headers });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching dynamic filters:', error);
            delete locationsFilterPromiseCache[cacheKey];
            return { status: false, data: [] };
        }
    })();

    locationsFilterPromiseCache[cacheKey] = {
        promise,
        time: now
    };

    return promise;
};



